import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase-config";
import { toast } from "react-toastify";
import "../../assets/styles/AdminShared.css";

const statusOptions = [
  "new",
  "reviewing",
  "in-progress",
  "waiting-on-client",
  "quoted-separately",
  "done",
];

const AdminCarePlanRequests = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState("");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const projectSnap = await getDoc(doc(db, "projects", projectId));
        if (!projectSnap.exists()) throw new Error("Project not found.");

        const data = projectSnap.data();
        setProjectName(data.projectName || "Website Project");
        setRequests((data.carePlanRequests || []).map((request, index) => ({
          id: request.id || `legacy-${index}-${request.submittedAt || "request"}`,
          status: "new",
          ...request,
        })));
      } catch (error) {
        toast.error("Failed to load Care Plan requests.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  const updateRequest = (index, field, value) => {
    setRequests(prev => prev.map((request, i) => i === index ? { ...request, [field]: value } : request));
  };

  const saveRequests = async () => {
    try {
      const cleanedRequests = requests.map(({ adminNotes, ...request }) => request);
      await updateDoc(doc(db, "projects", projectId), {
        carePlanRequests: cleanedRequests,
        latestCarePlanRequest: cleanedRequests[cleanedRequests.length - 1] || null,
      });
      setRequests(cleanedRequests);
      toast.success("Care Plan requests updated.");
    } catch (error) {
      toast.error("Failed to update Care Plan requests.");
    }
  };

  if (loading) return <div className="ap-loading"><p>Loading Care Plan requests...</p></div>;

  return (
    <div className="ap-page">
      <div className="ap-page-header">
        <a href={`/admin/project/${projectId}`} className="ap-back">← Back to project</a>
        <div className="ap-page-header-row">
          <div>
            <p className="ap-eyebrow">Admin — Care Plan</p>
            <h1 className="ap-title">{projectName}: Requests</h1>
            <p className="ap-subtitle">Track submitted Care Plan requests and update what the client sees in their request history.</p>
          </div>
          <button className="ap-btn-primary" onClick={saveRequests}>Save Requests</button>
        </div>
      </div>

      <div className="ap-section">
        <div className="ap-section-header">
          <h2 className="ap-section-title">All Requests</h2>
          <span className="ap-badge ap-badge--pending">{requests.length} Total</span>
        </div>
        <div className="ap-section-body">
          {requests.length ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {requests.map((request, index) => (
                <div key={request.id || index} className="ap-list-item" style={{ alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div className="ap-page-header-row" style={{ alignItems: "flex-start", marginBottom: "12px" }}>
                      <div>
                        <p style={{ fontFamily: "var(--font-ui)", fontWeight: 700, color: "var(--text-primary)", margin: "0 0 6px" }}>
                          {request.requestType || "Care Plan Request"} · {request.priority || "Normal"}
                        </p>
                        <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.875rem", color: "var(--text-tertiary)", margin: 0 }}>
                          {request.pageOrFeature || "No page specified"} {request.deadline ? `· Deadline: ${request.deadline}` : ""} {request.submittedAt ? `· Submitted: ${new Date(request.submittedAt).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      <span className={`ap-badge ${request.status === "done" ? "ap-badge--active" : "ap-badge--pending"}`}>
                        {request.status || "new"}
                      </span>
                    </div>

                    <p style={{ fontFamily: "var(--font-body)", fontSize: "0.9375rem", color: "var(--text-secondary)", lineHeight: 1.6, margin: "0 0 16px" }}>
                      {request.details}
                    </p>

                    <div className="ap-form-group">
                      <label>Status</label>
                      <select value={request.status || "new"} onChange={e => updateRequest(index, "status", e.target.value)}>
                        {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                      </select>
                    </div>
                    <div className="ap-btn-row">
                      <button
                        className="ap-btn-secondary"
                        onClick={() => navigate(`/admin/project/${projectId}/care-plan-requests/${encodeURIComponent(request.id || request.submittedAt || index)}`)}
                      >
                        Open Conversation →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ fontFamily: "var(--font-ui)", fontSize: "0.9375rem", color: "var(--text-tertiary)", margin: 0 }}>
              No Care Plan requests submitted yet.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCarePlanRequests;
